import React, { cloneElement } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { CalendarIcon, MessageCircleIcon, SparklesIcon, FlameIcon } from 'lucide-react';
export function CoachingPage() {
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-201">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-blush-50 to-white" data-id="element-202">
        <SparkleEffect count={80} data-id="element-203" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-204">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-205">
            1:1 Coaching
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-206">
            This isn't therapy. This is someone in your corner who's been
            through it and gets it.
          </motion.p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-207">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-208">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-209">
            <div className="aspect-square rounded-[3rem] bg-lavender-100 p-8 flex items-center justify-center relative overflow-hidden border border-lavender-200" data-id="element-210">
              <SparkleEffect count={40} data-id="element-211" />
              <div className="text-center z-10" data-id="element-212">
                <FlameIcon className="w-16 h-16 text-cta-500 mx-auto mb-4" data-id="element-213" />
                <h3 className="font-heading text-3xl text-plum-700 font-bold" data-id="element-214">
                  Who It's For
                </h3>
                <ul className="mt-6 space-y-3 text-left" data-id="element-215">
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-216">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-217" />{' '}
                    You're stuck and you know it.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-218">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-219" />{' '}
                    You're tired of just surviving.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-220">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-221" />{' '}
                    You need someone who won't judge your mess.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-222">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-223" />{' '}
                    You're ready to do the damn work.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-224">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-700 mb-6" data-id="element-225">
              What to Expect
            </h2>
            <ul className="space-y-6" data-id="element-226">
              {[{
              title: 'Real talk, not textbook advice',
              desc: "I don't do fluff. We're going to look at your life honestly and figure out exactly what needs to change."
            }, {
              title: 'Someone who actually gives a damn',
              desc: 'When you work with me, I am fully invested in your healing and your success.'
            }, {
              title: 'Accountability with love (and tough love)',
              desc: 'I will hold your hand, but I will also call you out on your excuses. Because you deserve better.'
            }, {
              title: 'Mindset shifts that stick',
              desc: "We aren't just putting a band-aid on it. We are rewiring how you think and operate."
            }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-227">
                  <div className="mt-1 w-2 h-2 rounded-full bg-cta-500 shrink-0" data-id="element-228" />
                  <div data-id="element-229">
                    <h4 className="font-bold text-plum-700 text-lg" data-id="element-230">
                      {item.title}
                    </h4>
                    <p className="text-plum-500 font-medium" data-id="element-231">{item.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-blush-50 mt-12" data-id="element-232">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-233">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-234">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-235">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-236" />

            {[{
            icon: <CalendarIcon data-id="element-237" />,
            title: '1. Book a Call',
            desc: "Schedule a free discovery call. Let's see if our vibes match."
          }, {
            icon: <MessageCircleIcon data-id="element-238" />,
            title: '2. Get Real',
            desc: 'We map out exactly where you are and where you want to be.'
          }, {
            icon: <SparklesIcon data-id="element-239" />,
            title: '3. Transform',
            desc: 'We do the work. You stop surviving and start living like a badass.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-240">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-241">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-242">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-243">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center px-4" data-id="element-244">
        <div className="max-w-2xl mx-auto bg-white p-12 rounded-[3rem] shadow-xl border border-blush-100 relative overflow-hidden" data-id="element-245">
          <SparkleEffect count={50} data-id="element-246" />
          <div className="relative z-10" data-id="element-247">
            <h2 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-248">
              Ready to invest in yourself?
            </h2>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-249">
              Investment starts at $1,200 for 3 months of deep, no-BS support.
            </p>
            <button className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-lg hover:-translate-y-1" data-id="element-250">
              <GlitterHeart size={20} variant="light" data-id="element-251" />
              Apply for Coaching
            </button>
          </div>
        </div>
      </section>
    </div>;
}